import type { AudioErrorKind, AudioPort, PlaybackState } from '../ports/audio';
import type { MediaSessionHandlers, MediaSessionPort } from '../ports/media-session';
import { effectiveUrl, findStation, isHttpOnly, neighbourStation, type Station } from './stations';
import { resolveStreamRoute, type StreamVia } from './stream-url';

/**
 * Krmilnik predvajanja: kaj se predvaja, kaj naredi transport, kako se
 * stanje porta prevede v stanje aplikacije.
 *
 * Domenski sloj ne pozna store-a — zato `onChange`. Store je nad domeno
 * (T1 §2) in ga v `main.ts` priklopi kompozicija, ne ta datoteka.
 */

export interface PlaybackSnapshot {
  stationId: string | null;
  playback: PlaybackState;
  attempt: number;
  /** Ali tekoči tok teče direktno ali prek Workerja. `null`, dokler ni izbire. */
  route: StreamVia | null;
  /**
   * Zakaj se je ustavilo. Brez tega bi zavrnjeno samodejno predvajanje
   * pokazalo »postaja ne odgovarja« — kar ni res in uporabnika pošlje
   * iskat napako tja, kje je ni (P4 §5).
   */
  error: AudioErrorKind | null;
}

export interface PlaybackController {
  play(stationId: string): Promise<void>;
  /** Predvajaj ali ustavi tekočo postajo; brez izbire začni s prvo. */
  toggle(): Promise<void>;
  stop(): void;
  next(): Promise<void>;
  previous(): Promise<void>;
  setVolume(v: number): void;
  setMuted(m: boolean): void;
  /** `null` = EQ ni dejaven; graf se zaradi tega ne postavi (T1 §4). */
  setEq(gains: number[] | null): void;
  /**
   * Uskladi pot toka z nastavitvami (faza 6).
   *
   * Vklop EQ ali vizualizatorja prestavi tok z direktne poti na Worker in
   * obratno; nova pot pomeni nov `src` in ta zahteva ponovno nalaganje.
   *
   * **Idempotentno.** Kadar je tekoči tok že naložen s pravo potjo, ne naredi
   * ničesar — drsnik, potegnjen čez ničlo in nazaj, bi sicer tok prekinil
   * dvakrat zapored. Kadar nič ne igra, si dejanje samo zapomni, da mora
   * naslednje predvajanje skozi polni `load()`.
   */
  syncRoute(): Promise<void>;
  snapshot(): PlaybackSnapshot;
  dispose(): void;
}

export interface PlaybackDeps {
  port: AudioPort;
  /**
   * Vse postaje — vir za iskanje po `id`.
   *
   * Funkcija, ne polje: od faze 2 se seznam med tekom spreminja (dodajanje,
   * brisanje, urejanje) in zamrznjena kopija bi predvajala izbrisano postajo.
   */
  stations: () => readonly Station[];
  /**
   * Vrstni red za ⏮ ⏭ — **trenutno viden seznam**, filtriran in razvrščen
   * (P2 §0.4). Če uporabnik gleda samo priljubljene, ⏭ ne sme skočiti na
   * postajo, ki je na zaslonu ni. Brez tega je enak `stations`.
   */
  transportOrder?: () => readonly Station[];
  onChange(snapshot: PlaybackSnapshot): void;
  session?: MediaSessionPort | null;
  volume?: number;
  muted?: boolean;
  /** Osnova za `/api/*`; testi jo podajo izrecno. */
  base?: string;
  /**
   * Nadgradnja sheme HTTP → HTTPS prek Workerja (T4 §3.1). Neobvezna:
   * brez nje samo-HTTP postaja teče prek proxyja, kar deluje, le počasneje.
   *
   * Rezultat gre prek `onResolved` v `Station.urlResolved` (T2 §11, korak 4),
   * zato je poizvedba enkratna in ne enkratna-na-sejo.
   */
  resolveHttps?: (url: string) => Promise<string | null>;
  /**
   * Sonda je našla HTTPS različico. Klicno mesto jo zapiše v shrambo —
   * brez tega bi vsak zagon poizvedoval znova (T2 §11, korak 4: »Rezultat
   * shrani — ne preverjaj ob vsakem predvajanju«).
   */
  onResolved?: (stationId: string, url: string) => void;
  /**
   * Postaja je bila zagnana. Vir podatka za razvrščanje »nedavno« in za
   * `playCount`; domena časa ne bere sama, da ostane testabilna.
   */
  onPlayed?: (stationId: string) => void;
  /**
   * Ali aplikacija potrebuje Web Audio graf — torej ali EQ ni raven ali je
   * vizualizator vklopljen (faza 6, `domain/eq.ts`).
   *
   * Funkcija, ne vrednost: nastavitve se med tekom spreminjajo in zamrznjena
   * kopija bi po prvem preklopu vlekla tok po napačni poti. Brez nje je
   * odgovor `false` — golo predvajanje z izvora, kot v fazah 1–5.
   */
  needsAudioGraph?: () => boolean;
}

/** Stanja, v katerih uporabnik pričakuje, da ga gumb ustavi, ne zažene. */
function isActive(state: PlaybackState): boolean {
  return state === 'loading' || state === 'playing' || state === 'reconnecting';
}

export function createPlayback(deps: PlaybackDeps): PlaybackController {
  const { port, onChange } = deps;
  const session = deps.session ?? null;

  const allStations = (): readonly Station[] => deps.stations();
  const transportOrder = (): readonly Station[] =>
    deps.transportOrder === undefined ? deps.stations() : deps.transportOrder();

  let volume = deps.volume ?? 1;
  let muted = deps.muted ?? false;

  /**
   * Naložen tok teče po poti, ki je veljala ob nalaganju — in ta pot je zdaj
   * zastarela (uporabnik je preklopil EQ ali vizualizator). Naslednje
   * predvajanje mora skozi polni `load()`, ne skozi `resume()`.
   */
  let routeStale = false;

  /**
   * Ali je bil tekoči tok naložen z Web Audio grafom; `null`, kadar ni naložen.
   *
   * Obstaja zaradi **idempotentnosti `syncRoute()`**: brez nje bi vsak premik
   * drsnika čez 0 dB znova naložil tok, tudi kadar bi bila nova pot enaka
   * tekoči. Vlečenje s +5 na −5 gre skozi ničlo in bi tok prekinilo dvakrat.
   */
  let loadedGraph: boolean | null = null;

  let snapshot: PlaybackSnapshot = {
    stationId: null,
    playback: 'idle',
    attempt: 0,
    route: null,
    error: null,
  };

  function publish(patch: Partial<PlaybackSnapshot>): void {
    snapshot = { ...snapshot, ...patch };
    onChange(snapshot);
  }

  // Vrstni red je pomemben: adapter odda `error` pred `statechange`, zato je
  // vzrok znan, preden se stanje prevesi v `error` ali `reconnecting`.
  const unsubscribeError = port.on('error', ({ kind }) => {
    publish({ error: kind });
  });

  /**
   * Dejanja medijske seje, znova prijavljena, ko zvok res steče.
   *
   * iOS medijsko sejo ustvari šele ob predvajanju. Prijava ob zagonu
   * aplikacije se izgubi in sistem ponudi svoja privzeta dejanja — na
   * zaklenjenem zaslonu se namesto ⏮ ⏭ pokažeta −10 s in +10 s, česar
   * živ tok ne more izvesti (opaženo na napravi 2026-07-19).
   */
  let sessionHandlers: MediaSessionHandlers | null = null;

  const unsubscribeState = port.on('statechange', ({ state, attempt }) => {
    const settled = state === 'idle' || state === 'loading' || state === 'playing';
    publish({ playback: state, attempt, ...(settled ? { error: null } : {}) });
    session?.setPlaybackState(state);

    if (state === 'playing' && sessionHandlers !== null) session?.setHandlers(sessionHandlers);
  });

  /**
   * Razrešeni HTTPS naslovi za to sejo.
   *
   * Od faze 2 gre uspešen rezultat prek `onResolved` tudi v shrambo, zato je
   * ta `Map` v drugi seji večinoma prazen in vseeno potreben: hrani tudi
   * **neuspehe** (`null`), ki se ne shranjujejo — postaja lahko HTTPS dobi
   * jutri in trajen »nima ga« bi to za vedno zaklenil.
   */
  const upgraded = new Map<string, string | null>();

  /**
   * Postaja z upoštevano nadgradnjo sheme.
   *
   * Poizvedba teče samo pri samo-HTTP postajah. Cena je en zahtevek pred
   * prvim predvajanjem, dobiček pa direktna pot namesto proxyja — kar je pri
   * postaji, ki jo primarni uporabnik največ posluša, razlika med tekočim in
   * zatikajočim se zvokom v avtu (T2 §12).
   */
  function withUpgrade(station: Station, resolved: string | null): Station {
    return resolved === null ? station : { ...station, urlResolved: resolved, isHttps: true };
  }

  /**
   * Postaja, kadar je nadgradnja sheme že znana — sicer `null`.
   *
   * Obstaja zato, da menjava postaje ostane **sinhrona**: večina postaj je
   * HTTPS in poizvedbe ne rabi, zato jih ni razloga peljati skozi obljubo
   * (glej `startPlayback`).
   */
  function upgradedNow(station: Station): Station | null {
    if (deps.resolveHttps === undefined || !isHttpOnly(station)) return station;
    if (!upgraded.has(station.id)) return null;
    return withUpgrade(station, upgraded.get(station.id) ?? null);
  }

  async function withResolvedScheme(station: Station): Promise<Station> {
    if (deps.resolveHttps === undefined || !isHttpOnly(station)) return station;

    if (!upgraded.has(station.id)) {
      const resolved = await deps.resolveHttps(effectiveUrl(station));
      upgraded.set(station.id, resolved);
      // Samo uspeh gre v shrambo. Neuspeh ostane v seji: postaja lahko HTTPS
      // dobi kasneje in trajen zapis »nima ga« bi jo za vedno pustil na proxyju.
      if (resolved !== null) deps.onResolved?.(station.id, resolved);
    }

    return withUpgrade(station, upgraded.get(station.id) ?? null);
  }

  /**
   * Zažene predvajanje **brez enega samega `await` pred `audio.play()`**.
   *
   * `port.load()` je označen `async`, a v telesu nima čakanja, zato steče do
   * konca sinhrono. Namerno ga zato ne čakamo: `port.play()` mora stati v
   * isti nalogi kot uporabnikova gesta, sicer WebKit predvajanje zavrne in
   * iOS po nekaj sekundah medijski center preda naslednji aplikaciji.
   */
  function startPlayback(station: Station): Promise<void> {
    /**
     * 🔴 **Pravilo proxyja** (T1 §4). Graf potrebuje CORS, CORS pomeni Worker.
     *
     * Ista vrednost gre v obe smeri — v izbiro poti **in** v adapter. Če bi se
     * razšli, bi bil izid najhujši možen: adapter bi postavil graf na tok brez
     * CORS in `createMediaElementSource()` bi oddal ničle. Zvok utihne brez
     * napake in klica ni mogoče razveljaviti (R3 §1).
     */
    const wantsAudioGraph = deps.needsAudioGraph?.() ?? false;
    loadedGraph = wantsAudioGraph;
    routeStale = false;

    const route = resolveStreamRoute(station, {
      needsAudioGraph: wantsAudioGraph,
      /**
       * HLS proxyja ne prenese (glej `resolveStreamRoute`), zato je pri njem
       * edino vprašanje, ali sme nastati graf — in to ve samo adapter: pri
       * `hls.js` element bere `blob:` z našega izvora, pri native HLS postajo.
       */
      hlsMse: port.capabilities.hlsMse,
      ...(deps.base === undefined ? {} : { base: deps.base }),
    });

    publish({ stationId: station.id, route: route.via, attempt: 0, error: null });
    session?.setNowPlaying({ title: station.name });
    deps.onPlayed?.(station.id);

    void port.load(station, { route, volume, muted, wantsAudioGraph });
    return port.play();
  }

  function play(stationId: string): Promise<void> {
    const found = findStation(allStations(), stationId);
    if (found === null) return Promise.resolve();

    const known = upgradedNow(found);
    if (known !== null) return startPlayback(known);

    // Samo tu se izgubi sinhronost — pri postaji, ki jo je treba šele
    // preveriti. Rezultat gre v shrambo, torej se to zgodi enkrat.
    return withResolvedScheme(found).then(startPlayback);
  }

  function stop(): void {
    port.stop();
    // Vir je razstavljen: naslednji zagon gre skozi `load()` in bo pot izbral
    // znova. Brez tega bi `syncRoute()` primerjal z zapisom, ki nima elementa.
    loadedGraph = null;
    // `port.stop()` odda `idle`, a `stationId` obstane. Brez čiščenja
    // `media-metadata.ts` po tej poti nikoli ne pošlje `setNowPlaying(null)` in
    // OS kartica »Now Playing« ostane vidna do konca seje. Klicno mesto je
    // brisanje tekoče postaje (glej `main.ts`); gumb »Ustavi« v aplikaciji
    // pavzira in te poti ne uporabi (P4 §8).
    session?.detach();
    publish({ stationId: null, route: null, error: null });
  }

  /**
   * Ali sme nadaljevanje mimo `load()`.
   *
   * Kadar je isti vir še **živ in naložen**, gre nadaljevanje neposredno v
   * `resume()`, ki predvajanje sproži sinhrono, brez ponovnega medpomnjenja.
   *
   * 🔴 **`port.canResume` je nujen del pogoja.** iOS ob pavzi v ozadju element
   * izprazni; `loadedStationId` ostane isti (vir ni razstavljen), a `resume()`
   * takega elementa ne oživi. Brez preverjanja zdravja bi `toggle()` klical
   * `resume()` na mrtvem elementu — gumb bi obstal in postajo bi bilo treba
   * ročno klikniti znova (prijavljeno z naprave 2026-07-20). Ob `false` pade na
   * `play()`, ki naredi reload — ista pot kot klik na postajo in kot ⏮ ⏭, ki z
   * zaklenjenega zaslona **delujeta**.
   *
   * 🔴 **`routeStale` je drugi nujni del.** Naložen tok nosi pot, ki je veljala
   * ob nalaganju. Če uporabnik med pavzo vklopi EQ, `resume()` oživi **stari**
   * element s staro potjo in EQ tiho ne prime do naslednje menjave postaje —
   * videti je kot pokvarjen ekvalizer. Zastavica takrat nadaljevanje spelje
   * skozi polni `play()`, ki tok naloži po novi poti.
   */
  function canResume(): boolean {
    return (
      !routeStale &&
      snapshot.stationId !== null &&
      port.loadedStationId === snapshot.stationId &&
      port.canResume
    );
  }

  function step(direction: 1 | -1): Promise<void> {
    const target = neighbourStation(transportOrder(), snapshot.stationId, direction);
    if (target === null) return Promise.resolve();
    return play(target.id);
  }

  const controller: PlaybackController = {
    play,
    stop,

    toggle() {
      if (isActive(snapshot.playback)) {
        // Pavza, ne razstavljanje: sicer nadaljevanje na zaklenjenem
        // zaslonu iOS ne steče.
        port.pause();
        return Promise.resolve();
      }

      // Brez `await` pred tem klicem — nosi uporabnikovo gesto.
      if (canResume()) return port.resume();

      const target = snapshot.stationId ?? transportOrder()[0]?.id ?? null;
      if (target === null) return Promise.resolve();
      return play(target);
    },

    // Ne-`async`: obljubo vrneta, ne čakata je. Vsak `await` tu bi menjavo
    // postaje z zaklenjenega zaslona podrl.
    next() {
      return step(1);
    },

    previous() {
      return step(-1);
    },

    setVolume(v) {
      volume = Math.min(1, Math.max(0, v));
      port.setVolume(volume);
    },

    setMuted(m) {
      muted = m;
      port.setMuted(m);
    },

    setEq(gains) {
      port.setEq(gains);
    },

    /**
     * Ponovno naloži po **novi** poti (T1 §4).
     *
     * Uporabnik je preklop sprožil sam, zato tudi takoj sliši učinek; kratek
     * premor ob ponovnem medpomnjenju je razumljiv, tiho neukrepanje pa bi bilo
     * videti kot pokvarjen EQ (odločeno 2026-07-21, D3 #19).
     *
     * Kadar nič ne igra, se tok ne zaganja — dovolj je, da je nadaljevanje
     * označeno za zastarelo in gre naslednjič skozi polni `load()`.
     */
    syncRoute() {
      const want = deps.needsAudioGraph?.() ?? false;

      // Naloženo je že prav. Drsnik, potegnjen čez ničlo in nazaj, ne sme
      // prekiniti toka dvakrat — ali sploh.
      if (loadedGraph === null || want === loadedGraph) {
        routeStale = false;
        return Promise.resolve();
      }

      routeStale = true;
      if (!isActive(snapshot.playback)) return Promise.resolve();
      const target = snapshot.stationId;
      if (target === null) return Promise.resolve();
      return play(target);
    },

    snapshot() {
      return snapshot;
    },

    dispose() {
      unsubscribeState();
      unsubscribeError();
      session?.detach();
    },
  };

  sessionHandlers = {
    onPlay: () => {
      void controller.toggle();
    },
    // Pavza obdrži vir; `stop` ga razstavi. Na iOS je ta razlika usodna —
    // po razstavljenem viru se predvajanje na zaklenjenem zaslonu ne zažene.
    onPause: () => {
      port.pause();
    },
    /**
     * Dejanje `stop` medijske seje pavzira, ne razstavi.
     *
     * Na iOS je to brez učinka: `StopCommand` ni med ukazi, ki gredo v
     * `MRMediaRemoteSetSupportedCommands()`, zato dejanje ni nikoli dostavljeno.
     * **Android ga dostavi** — in tam je razstavljanje pokvarilo krmiljenje z
     * zaklenjenega zaslona.
     *
     * Vzrok je v Chromiumu, `MediaSessionHelper.mediaSessionStateChanged()`: ko
     * seja neha biti krmiljena, se obvestilo skrije. Odklenjeno z zamikom 500 ms,
     * ki ga naslednja posodobitev prekliče — zato senčnik deluje. Kadar je
     * keyguard na zaslonu, pa **takoj** (`NotificationManager.cancel()`), in
     * kartica se pod uporabnikovim prstom podre ter znova sestavi.
     *
     * `releaseElement()` (`removeAttribute('src')` + `load()`) je natanko tisto,
     * kar sejo vzame iz krmiljenega stanja. Pavza je ne.
     *
     * Ustreza tudi besedišču iz P4 §8: gumb v aplikaciji nosi napis »Ustavi« in
     * že doslej pavzira. Ena beseda, eno vedenje.
     */
    onStop: () => {
      port.pause();
    },
    onPrevious: () => {
      void controller.previous();
    },
    onNext: () => {
      void controller.next();
    },
  };

  session?.setHandlers(sessionHandlers);

  return controller;
}
